import * as React from 'react';
import { useT } from '../lib/i18n.js';
import { DomainApiError } from '../lib/api-error.js';
import {
  deleteCalendarEvent,
  getCalendarEvent,
  updateCalendarEvent,
  type CalendarEvent,
  type CalendarEventStatus,
} from '../lib/calendar-api.js';
import { formatDateTime } from '@reos/i18n';

/**
 * Detail události v kalendáři (krok CRM3). Vzorek je `task-drawer.tsx`:
 * načte se při otevření, editace jde přes API a změnu hlásí rodiči,
 * aby překreslil kalendář.
 *
 * Třídy `.rh-demand-drawer` jsou schválně PŘEVRÁTÉ z poptávek — je to tatáž
 * obrazovka v jiném kontextu (CRM2 to udělalo stejně pro úkoly a třetí sada
 * týchž stylů by se rozjela).
 */
export interface CalendarEventDrawerProps {
  readonly eventId: string;
  readonly onClose: () => void;
  readonly onChanged: () => void;
  readonly onDeleted: () => void;
}

/** Krok UXV4c: odkaz na nabor na miste s udalosti (zakazka, adresa = misto udalosti). */
export function naborHref(event: Pick<CalendarEvent, 'id' | 'caseId' | 'location'>): string {
  const params = new URLSearchParams({ eventId: event.id });
  if (event.caseId !== null && event.caseId !== undefined) params.set('caseId', event.caseId);
  if (event.location !== null && event.location !== undefined && event.location !== '') params.set('address', event.location);
  return `/nabor?${params.toString()}`;
}

const STATUSES: readonly CalendarEventStatus[] = ['confirmed', 'tentative', 'cancelled'];

export function CalendarEventDrawer({
  eventId,
  onClose,
  onChanged,
  onDeleted,
}: CalendarEventDrawerProps): React.JSX.Element {
  const t = useT();
  const [event, setEvent] = React.useState<CalendarEvent | null>(null);
  const [title, setTitle] = React.useState('');
  const [status, setStatus] = React.useState<CalendarEventStatus>('confirmed');
  const [location, setLocation] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const applyEvent = React.useCallback((value: CalendarEvent) => {
    setEvent(value);
    setTitle(value.title);
    setStatus(value.status);
    setLocation(value.location ?? '');
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setError(null);
    void getCalendarEvent(eventId)
      .then((value) => {
        if (!cancelled) applyEvent(value);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setEvent(null);
        setError(e instanceof DomainApiError ? e.message : t('webAdmin.calendar.drawer.loadFailed'));
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, applyEvent, t]);

  async function save(): Promise<void> {
    if (event === null) return;
    setError(null);
    setNotice(null);
    try {
      const updated = await updateCalendarEvent(event.id, {
        title: title.trim(),
        status,
        location: location.trim(),
      });
      applyEvent(updated);
      setNotice(t('webAdmin.calendar.drawer.saved'));
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.calendar.saveFailed'));
    }
  }

  async function remove(): Promise<void> {
    if (event === null) return;
    if (typeof window !== 'undefined' && !window.confirm(t('webAdmin.calendar.drawer.deleteConfirm'))) return;
    setError(null);
    try {
      await deleteCalendarEvent(event.id);
      onDeleted();
    } catch (e: unknown) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.calendar.actionFailed'));
    }
  }

  return (
    <aside className="rh-demand-drawer" data-testid="calendar-drawer" aria-label={t('webAdmin.calendar.drawer.heading')}>
      <header className="rh-demand-drawer__header">
        <h2 className="rh-kanban-card__title">{event?.title ?? t('webAdmin.calendar.drawer.heading')}</h2>
        <button type="button" className="rh-btn" onClick={onClose} data-testid="calendar-drawer-close">
          {t('webAdmin.calendar.drawer.close')}
        </button>
      </header>
      {error !== null && (
        <p role="alert" className="rh-demand-drawer__error" data-testid="calendar-drawer-error">
          {error}
        </p>
      )}
      {notice !== null && (
        <p role="status" className="mb-2 text-sm" data-testid="calendar-drawer-notice">
          {notice}
        </p>
      )}
      {event !== null && (
        <>
          <section className="rh-demand-drawer__section">
            <div className="rh-demand-form">
              <label>
                {t('webAdmin.calendar.form.fieldTitle')}
                <input
                  data-testid="calendar-drawer-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label>
                {t('webAdmin.calendar.form.location')}
                <input
                  data-testid="calendar-drawer-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </label>
              <label>
                {t('webAdmin.calendar.drawer.status')}
                <select
                  data-testid="calendar-drawer-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as CalendarEventStatus)}
                >
                  {STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {t(`webAdmin.calendar.status.${value}` as 'webAdmin.calendar.status.confirmed')}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="rh-demand-drawer__moves">
              <button
                type="button"
                className="rh-btn rh-btn--primary"
                data-testid="calendar-drawer-save"
                disabled={!event.canUpdate}
                onClick={() => void save()}
              >
                {t('webAdmin.calendar.drawer.save')}
              </button>
              {event.canDelete && (
                <button
                  type="button"
                  className="rh-btn"
                  data-testid="calendar-drawer-delete"
                  onClick={() => void remove()}
                >
                  {t('webAdmin.calendar.drawer.delete')}
                </button>
              )}
            </div>
          </section>

          <section className="rh-demand-drawer__section">
            <h3 className="rh-kanban-card__title">{t('webAdmin.calendar.drawer.links')}</h3>
            <dl className="rh-demand-drawer__grid">
              <dt>{t('webAdmin.calendar.form.type')}</dt>
              <dd data-testid="calendar-drawer-type">{event.typeLabel}</dd>
              <dt>{t('webAdmin.calendar.form.startsAt')}</dt>
              <dd data-testid="calendar-drawer-start">{formatDateTime(new Date(event.startsAt), { locale: 'cs-CZ' })}</dd>
              <dt>{t('webAdmin.calendar.form.endsAt')}</dt>
              <dd data-testid="calendar-drawer-end">{formatDateTime(new Date(event.endsAt), { locale: 'cs-CZ' })}</dd>
              <dt>{t('webAdmin.calendar.form.case')}</dt>
              <dd>{event.caseId ?? '—'}</dd>
              <dt>{t('webAdmin.calendar.form.party')}</dt>
              <dd>{event.partyId ?? '—'}</dd>
              <dt>{t('webAdmin.calendar.form.demand')}</dt>
              <dd data-testid="calendar-drawer-demand">{event.demandId ?? '—'}</dd>
            </dl>
          </section>

          {/* Krok UXV4c: nabor na miste rovnou z udalosti — adresa a zakazka predvyplnene. */}
          <section className="rh-demand-drawer__section">
            <a className="rh-btn rh-btn--primary" href={naborHref(event)} data-testid="calendar-drawer-intake">
              {t('webAdmin.fieldIntake.entry.fromCalendar')}
            </a>
          </section>

          <section className="rh-demand-drawer__section">
            <h3 className="rh-kanban-card__title">{t('webAdmin.calendar.drawer.reminderHeading')}</h3>
            <p data-testid="calendar-drawer-reminder">
              {event.reminderMinutes === 0
                ? t('webAdmin.calendar.drawer.reminderNone')
                : event.reminderSentAt !== null
                  ? t('webAdmin.calendar.drawer.reminderSent', {
                      at: formatDateTime(new Date(event.reminderSentAt), { locale: 'cs-CZ' }),
                    })
                  : t('webAdmin.calendar.drawer.reminderPending', {
                      at: formatDateTime(new Date(event.startsAt), { locale: 'cs-CZ' }),
                    })}
            </p>
          </section>
        </>
      )}
    </aside>
  );
}
